import React, { useState } from 'react';
import type { ModalComponentProps } from '@components/modals/constants';
import { AccountBalance as AccountBalanceIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import { useStripeConnect } from '../store/hooks';

import { StripeConnectRedirect } from './StripeConnectRedirect';

interface StripeConnectInfoModalProps extends ModalComponentProps {
  modalProps: Record<string, unknown> & {
    onConnectComplete?: () => void;
    redirectDelay?: number; // in seconds, default 5
    returnUrl?: string; // Optional custom return URL for Stripe onboarding
  };
}

const StripeConnectInfoModal: React.FC<StripeConnectInfoModalProps> = ({
  modalProps,
  closeModal,
}) => {
  const { onConnectComplete, redirectDelay = 5, returnUrl } = modalProps;
  const { startOnboarding, isCreating } = useStripeConnect();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [onboardingUrl, setOnboardingUrl] = useState<string | null>(null);

  const handleConnectStripe = async () => {
    try {
      setIsRedirecting(true);
      // Pass the custom returnUrl if provided
      const result = await startOnboarding(returnUrl);

      if (result.onboarding_url) {
        setOnboardingUrl(result.onboarding_url);
      }
    } catch (err) {
      console.error('Failed to start Stripe onboarding:', err);
      setIsRedirecting(false);
    }
  };

  const handleRedirect = () => {
    if (onboardingUrl) {
      window.location.href = onboardingUrl;
      onConnectComplete?.();
    }
  };

  return (
    <>
      <DialogTitle>Connect Your Stripe Account</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ py: 2 }}>
          <Typography variant="h6" gutterBottom>
            To send payment requests, you&apos;ll need to connect your Stripe account.
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            This only takes a few minutes and ensures secure payment processing and bank deposits.
          </Typography>

          {isRedirecting && onboardingUrl && (
            <StripeConnectRedirect
              variant="modal"
              onCountdownComplete={handleRedirect}
              redirectDelay={redirectDelay}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={closeModal} disabled={isRedirecting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConnectStripe}
          disabled={isCreating || isRedirecting}
          startIcon={
            isCreating || isRedirecting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <AccountBalanceIcon />
            )
          }
        >
          {isCreating || isRedirecting ? 'Connecting...' : 'Connect Stripe Account'}
        </Button>
      </DialogActions>
    </>
  );
};

export default StripeConnectInfoModal;
